import { useContext, useEffect, useState } from 'react'
import { CheckCircle2, Loader2, Radio } from 'lucide-react'
import { cn } from '@/lib/utils'
import useSyncStore from '@/store/SyncStore'
import { AppContext } from '@/context'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/** Duration to show completion indicator before hiding */
const COMPLETE_VISIBILITY_DURATION_MS = 1600

/**
 * Sync status indicator that shows real-time synchronization feedback.
 *
 * Displays a floating indicator in the bottom-right corner that shows:
 * - Syncing state with animated spinner during active sync operations
 * - Completion state with checkmark when sync finishes
 * - Auto-hide behavior after completion
 * - Accessible status updates via aria-live
 *
 * Features:
 * - Non-blocking floating design
 * - Smooth animations and transitions
 * - Accessible status announcements
 * - Auto-hide after completion
 * - Visual feedback for pending operations
 *
 * State Management:
 * - Integrates with SyncStore for real-time sync status
 * - Tracks pending operations count
 * - Monitors completion timestamps
 *
 * @example
 * ```tsx
 * // Typically used in app layout
 * function App() {
 *   return (
 *     <>
 *       <MainContent />
 *       <SyncIndicator />
 *     </>
 *   );
 * }
 * ```
 */
export function SyncIndicator() {
  const pending = useSyncStore((state) => state.pending)
  const lastCompletedAt = useSyncStore((state) => state.lastCompletedAt)
  const [showJustCompleted, setShowJustCompleted] = useState(false)
  const { isLeader, isDbLoading } = useContext(AppContext)

  useEffect(() => {
    if (pending > 0) {
      setShowJustCompleted(false)
      return
    }

    if (!lastCompletedAt) {
      setShowJustCompleted(false)
      return
    }

    setShowJustCompleted(true)
    const timeout = window.setTimeout(() => {
      setShowJustCompleted(false)
    }, COMPLETE_VISIBILITY_DURATION_MS)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [pending, lastCompletedAt])

  // A follower tab that's done loading but isn't the leader should surface
  // that the primary tab is handling sync -- otherwise the lack of network
  // activity on this tab looks like a broken offline state.
  const isFollower = !isLeader && !isDbLoading

  const isVisible = pending > 0 || showJustCompleted || isFollower
  if (!isVisible) {
    return null
  }

  const isSyncing = pending > 0
  const showFollowerOnly = isFollower && !isSyncing && !showJustCompleted
  const Icon = isSyncing ? Loader2 : showFollowerOnly ? Radio : CheckCircle2
  const message = isSyncing ? 'Syncing changes…' : 'All changes saved'
  const tooltip = showFollowerOnly ? 'Syncing' : message

  const badge = (
    <div
      className={cn(
        'flex items-center gap-1.5 rounded-md bg-foreground/60 text-[11px] font-medium text-background shadow-sm backdrop-blur transition-transform duration-200',
        showFollowerOnly ? 'p-1.5' : 'px-2.5 py-1',
        isSyncing ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-90',
      )}
    >
      <Icon
        className={cn('h-3 w-3', isSyncing && 'animate-spin')}
        aria-hidden="true"
      />
      {!showFollowerOnly && <span>{message}</span>}
    </div>
  )

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={tooltip}
      className="fixed bottom-6 left-6 z-50"
    >
      {showFollowerOnly ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={tooltip}
                className="cursor-default rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {badge}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" align="start">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <div className="pointer-events-none">{badge}</div>
      )}
    </div>
  )
}

export default SyncIndicator
